import { useTranslation } from "react-i18next";
import {
    TEXT_FIELD_TYPE,
    DATE_RANGE_FIELD_TYPE,
    BOOLEAN_FIELD_TYPE,
    NUMERIC_RANGE_FIELD_TYPE
} from "../../helpers/constants";
import { IconButton } from "@mui/material";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import EditIcon from '@mui/icons-material/Edit';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

import { MatchInvoiceWithTransferDialog } from "./MatchInvoiceWithTransferDialog";

import { formatCurrency } from "../../components/common/PriceDisplay";
import { AgreementPassportButton } from "../AgreementsPage/AgreementPassportDialog/AgreementPassportButton";

import { useUpdateInvoiceMutation } from "../../services/bankingService";
import toast from "react-hot-toast";
import { useToasts } from "../../hooks/useToasts";
import { SearchOtherClientDialog } from "../../components/dialog/ClientDiscoveryDialog/SearchOtherClientDialog";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";

import { boolToString } from "../../helpers/methods";
export const TABLE_NAME = "invoice_table";

export const useTableFieldsConfig = () => {
    const { t } = useTranslation();

    const { openDialog: openTransferForInvoiceDialog } = useDialogBetter();
    const { openDialog: openCustomerForInvoiceDialog, closeDialog: closeCustomerForInvoiceDialog } = useDialogBetter();
    
    const handleClickOpenMatchInvoiceWithTransferDialog = (e, invoiceId, transferId, productNumber) => {
        e.stopPropagation();
        e.preventDefault();
        openTransferForInvoiceDialog({
            Component: MatchInvoiceWithTransferDialog,
            componentProps: {
            invoiceId: invoiceId,
            transferId: transferId,
            readOnly:false,
            initialParams: new URLSearchParams({agreement_product: productNumber, has_invoice: false})
            },
        });
    };

    const { onFormErrors, onServerError } = useToasts();
    const { mutate: updateInvoice } = useUpdateInvoiceMutation({
            onSuccess: () => {
                toast.custom(
                    () => <SnackbarSuccess message={t("successfullyMerged")} />,
                    {
                        position: "bottom-right",
                    },
                );
            },
            onError: (error) => {
                onServerError(error);
            },
        });
    

    const handleClickOpenMatchInvoiceWithCustomerDialog = (e, invoiceId) => {
        e.stopPropagation();
        e.preventDefault();
        openCustomerForInvoiceDialog({
            Component: SearchOtherClientDialog,
            componentProps: {
            title: t("clients.ClientSearchTable.searchClient"),
            defaultSearchParams: {has_agreements: true},
            onCustomerSelected: (customer) => {
                updateInvoice({id:invoiceId, data: {invoice_customer: customer.id}})
                closeCustomerForInvoiceDialog()
            },
            },
        });
    };

    return [
        {
            name: "agreement_nr",
            getValueCallback: (rowData) => rowData.agreement_nr,
            label: t("bankings.agreement_nr"),
            filterType: TEXT_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "agreement_notarial_deed",
            getValueCallback: (rowData) => rowData.agreement_notarial_deed,
            label: t("bankings.agreement_notarial_deed"),
            filterType: TEXT_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "agreement_notarial_sign_date",
            getValueCallback: (rowData) => rowData.agreement_notarial_sign_date,
            label: t("bankings.agreement_notarial_sign_date"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        // {
        //     name: "passport",
        //     getValueCallback: (rowData) => rowData.invoice_agreement_id ? <AgreementPassportButton agreement={{id:rowData.invoice_agreement_id}} /> : null,
        //     label: t("bankings.agreement_passport"),
        //     getFilterFieldConfigCallback: () => undefined,
        //     sortable: false,
        // },
        {
            name: "invoice_customer_code",
            getValueCallback: (rowData) => rowData.invoice_customer_code,
            label: t("bankings.invoice_customer_code"),
            filterType: TEXT_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "customer_common_name",
            getValueCallback: (rowData) => (
                rowData.invoice_customer_id ? 
                rowData.customer_common_name :
                <IconButton
                    color={"primary"}
                    onClick={(e) => handleClickOpenMatchInvoiceWithCustomerDialog(e, rowData.id)}
                >
                    <PersonSearchIcon />
                </IconButton>
            ),
            label: t("bankings.customer_common_name"),
            filterType: TEXT_FIELD_TYPE,
            sortable: false,
        },
        {
            name: "invoice_product_number",
            getValueCallback: (rowData) => rowData.invoice_product_number,
            label: t("bankings.invoice_product_number"),
            filterType: TEXT_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "invoice_amount",
            getValueCallback: (rowData) => formatCurrency(rowData.invoice_amount),
            label: t("bankings.invoice_amount"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "transfer_date",
            getValueCallback: (rowData) => rowData.transfer_date,
            label: t("bankings.transfer_date"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "invoice_number",
            getValueCallback: (rowData) => rowData.invoice_number,
            label: t("bankings.invoice_number"),
            filterType: TEXT_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "invoice_sale_date",
            getValueCallback: (rowData) => rowData.invoice_sale_date,
            label: t("bankings.invoice_sale_date"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "invoice_issue_date",
            getValueCallback: (rowData) => rowData.invoice_issue_date,
            label: t("bankings.invoice_issue_date"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "separation_of_property",
            getValueCallback: (rowData) => boolToString(rowData.separation_of_property),
            label: t("bankings.separation_of_property"),
            filterType: BOOLEAN_FIELD_TYPE,
        },
        {
            name: "invoice_is_connected",
            getValueCallback: (rowData) => boolToString(rowData.invoice_is_connected),
            label: t("bankings.invoice_is_connected"),
            filterType: BOOLEAN_FIELD_TYPE,
        },
        // {
        //     name: "invoice_is_booked",
        //     getValueCallback: (rowData) => boolToString(rowData.invoice_is_booked),
        //     label: t("bankings.invoice_is_booked"),
        //     filterType: BOOLEAN_FIELD_TYPE,
        // },
        {
            name: "edit",
            getValueCallback: (rowData) => (
                rowData.invoice_transfer_id ? 
                <IconButton
                    color={"primary"}
                    onClick={(e) => handleClickOpenMatchInvoiceWithTransferDialog(e, rowData.id, rowData.invoice_transfer_id, rowData.invoice_product_number)}
                >
                <EditIcon />
                </IconButton>
                : 
                <IconButton
                    color={"primary"}
                    onClick={(e) => handleClickOpenMatchInvoiceWithTransferDialog(e, rowData.id, rowData.invoice_transfer_id, rowData.invoice_product_number)}
                >
                    <PersonSearchIcon />
                </IconButton>
            ),
            allowChangeVisibility: false,
            excludedFromExport: true,
        },
    ];
};
